import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import './LegalPage.css';

const legalSections = [
  {
    id: 'terms',
    title: 'Terms of Service',
    summary: 'Use TikPlat for lawful events, keep your account secure, and respect event-specific rules.',
    bullets: [
      'Accounts are personal; keep credentials private and notify us of unauthorized use.',
      'Listing or attending events must comply with local laws and venue requirements.',
      'Abusive behavior, fraud, or resale that breaches promoter rules can lead to suspension.'
    ]
  },
  {
    id: 'privacy',
    title: 'Privacy Policy',
    summary: 'We process data to create accounts, complete orders, prevent fraud, and deliver tickets.',
    bullets: [
      'We minimize data, encrypt sensitive fields, and use role-based access for employees.',
      'You can request access, correction, export, or deletion of your personal data where permitted.',
      'Third parties (payments, seats.io, analytics) only receive the data needed to perform their role.'
    ]
  },
  {
    id: 'cookies',
    title: 'Cookie Policy',
    summary: 'Cookies keep you signed in, remember cart selections, and help us measure product health.',
    bullets: [
      'Essential cookies power authentication, rate limiting, and seat selection.',
      'Analytics and marketing cookies are optional where required by law.',
      'Stripe may set its own cookies during checkout for security and fraud prevention.'
    ]
  },
  {
    id: 'refunds',
    title: 'Refunds & Cancellations',
    summary: 'Refunds follow the planner’s policy and applicable consumer laws.',
    bullets: [
      'Digital tickets are voided after a refund; chargebacks may restrict future purchases.',
      'Event changes or cancellations are communicated via the email on your account.',
      'For EU events, statutory consumer protections apply; contact us if you need help.'
    ]
  },
  {
    id: 'payments',
    title: 'Payments & PCI',
    summary: 'Card data is handled by Stripe; we do not store full card numbers or CVC codes.',
    bullets: [
      'Stripe provides PCI DSS compliance; we use their client and server SDKs.',
      '3D Secure may be required for higher-risk or higher-value transactions.',
      'Receipts are issued to the billing email you provide at checkout.'
    ]
  },
  {
    id: 'security',
    title: 'Security',
    summary: 'We protect tickets, accounts, and payments with encryption and layered defenses.',
    bullets: [
      'JWT-based auth with short-lived tokens; passwords are hashed with BCrypt.',
      'Rate limiting backed by Redis to reduce abuse; QR codes are encrypted at rest.',
      'We monitor for unusual login and checkout activity to flag potential fraud.'
    ]
  },
  {
    id: 'tickets',
    title: 'Digital Tickets',
    summary: 'Each ticket includes a secure QR code and metadata to verify authenticity.',
    bullets: [
      'Screenshots may not be accepted if the promoter requires animated codes.',
      'Do not publish QR codes; compromised codes can be invalidated.',
      'Seat transfers, if allowed, are subject to promoter rules and fees.'
    ]
  },
  {
    id: 'compliance',
    title: 'Compliance & Contact',
    summary: 'Reach us for data, legal, or safety questions. We respond quickly to verified requests.',
    bullets: [
      'Data Controller: TikPlat UG (forthcoming) — interim contact via legal@tikplat.com.',
      'Security issues: security@tikplat.com; include repro steps and impact.',
      'We aim to respond within one business day to legal, privacy, or safety matters.'
    ]
  }
];

const LegalPage = () => {
  const location = useLocation();

  useEffect(() => {
    const targetFromPath = location.pathname.split('/').filter(Boolean)[1];
    const targetFromHash = location.hash ? location.hash.replace('#', '') : '';
    const anchor = targetFromHash || targetFromPath;

    if (anchor) {
      const el = document.getElementById(anchor);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location]);

  return (
    <div className="legal-shell">
      <Helmet>
        <title>Legal, Privacy, and Compliance - TikPlat</title>
        <meta name="description" content="Terms, privacy, cookies, security, and refunds for TikPlat." />
      </Helmet>

      <section className="legal-hero">
        <div className="legal-hero-content">
          <p className="legal-pill">Trust & Compliance</p>
          <h1>Built for safe, transparent ticketing.</h1>
          <p className="legal-lede">
            This page summarizes our approach to terms, privacy, cookies, payments, and security. It does not replace
            the full agreements that will be published before launch.
          </p>
        </div>
      </section>

      <section className="legal-grid">
        {legalSections.map((section) => (
          <article key={section.id} id={section.id} className="legal-card">
            <p className="legal-eyebrow">{section.title}</p>
            <h2>{section.summary}</h2>
            <ul>
              {section.bullets.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <div className="legal-anchor">#{section.id}</div>
          </article>
        ))}
      </section>

      <section className="legal-meta">
        <p>
          Note: These summaries are for convenience and do not constitute legal advice or the final binding policies of
          TikPlat. Finalized documents (Terms of Service, Privacy Policy, Cookie Policy, Refund Policy) will be posted
          before production launch.
        </p>
      </section>
    </div>
  );
};

export default LegalPage;
